import React, { useState } from "react";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle } from "lucide-react";
import api, { formatError } from "../lib/api";

const CATEGORIES = ["customer_growth", "hiring", "automation", "operations", "finance", "branding", "web_digital", "local_business", "personal"];
const INTENTS = ["growth", "hire", "automate", "reduce_costs", "build", "improve", "plan", "brand"];
const INDUSTRIES = ["health", "real_estate", "restaurant", "salon", "retail", "tech", "consulting", "education", "logistics", "finance_industry"];

export default function AddExecutedTaskForm({ businessId, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    title: "", task_category: "customer_growth", task_intent: "growth",
    industry: "", client_type: "small_business", location_scope: "local",
    goal_summary: "", narrative_summary: "",
    time_taken_days: "", budget_min: "", budget_max: "",
    result_metrics: [{ key: "", value: "" }],
    proof_url: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const setMetric = (i, field, val) => {
    const updated = [...form.result_metrics];
    updated[i] = { ...updated[i], [field]: val };
    setForm(f => ({ ...f, result_metrics: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const metricsObj = {};
      form.result_metrics.forEach(({ key, value }) => {
        if (key.trim()) metricsObj[key.trim()] = value;
      });
      const payload = {
        ...form,
        time_taken_days: parseInt(form.time_taken_days),
        budget_min: parseInt(form.budget_min),
        budget_max: parseInt(form.budget_max),
        result_metrics: metricsObj,
        execution_method: {},
      };
      const { data } = await api.post("/api/records/", payload);
      setSuccess(true);
      setTimeout(() => onSuccess(data), 1000);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
        className="glass-card" style={{ padding: "2rem", textAlign: "center", marginBottom: "1.5rem" }}>
        <CheckCircle size={32} color="#6FFFCF" style={{ margin: "0 auto 0.8rem" }} />
        <div style={{ fontFamily: "Outfit, sans-serif", fontWeight: 700, color: "#1A1A1A" }}>Execution documented!</div>
        <p style={{ color: "#888", fontSize: "0.82rem", fontFamily: "Satoshi, sans-serif", marginTop: 4 }}>Your record has been submitted for review.</p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="glass-card" data-testid="add-record-form" style={{ padding: "1.8rem", marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.4rem" }}>
        <h3 style={{ fontFamily: "Outfit, sans-serif", fontWeight: 700, fontSize: "1rem", color: "#1A1A1A" }}>Document a success</h3>
        <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: "#AAA", fontSize: "1.2rem" }}>×</button>
      </div>

      {error && (
        <div style={{ display: "flex", gap: 8, background: "rgba(255,51,102,0.08)", border: "1px solid rgba(255,51,102,0.2)", borderRadius: 10, padding: "0.6rem 0.8rem", marginBottom: "1rem" }}>
          <AlertCircle size={14} color="var(--pink)" />
          <span style={{ fontSize: "0.78rem", color: "var(--pink)", fontFamily: "Satoshi, sans-serif" }}>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: "1rem" }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="form-label">What did you help with? *</label>
            <input data-testid="record-title-input" className="form-input" value={form.title} onChange={set("title")} placeholder="e.g. Increase salon bookings through local marketing" required />
          </div>

          <div>
            <label className="form-label">Category *</label>
            <select data-testid="record-category-select" className="form-input" value={form.task_category} onChange={set("task_category")}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
          </div>

          <div>
            <label className="form-label">Intent *</label>
            <select data-testid="record-intent-select" className="form-input" value={form.task_intent} onChange={set("task_intent")}>
              {INTENTS.map(i => <option key={i} value={i}>{i.replace(/_/g, " ")}</option>)}
            </select>
          </div>

          <div>
            <label className="form-label">Industry</label>
            <select data-testid="record-industry-select" className="form-input" value={form.industry} onChange={set("industry")}>
              <option value="">Any / General</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i.replace(/_/g, " ")}</option>)}
            </select>
          </div>

          <div>
            <label className="form-label">Location scope</label>
            <select data-testid="record-location-select" className="form-input" value={form.location_scope} onChange={set("location_scope")}>
              {["local", "city", "national", "global"].map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label className="form-label">What was the goal? *</label>
            <textarea data-testid="record-goal-input" className="form-input" value={form.goal_summary} onChange={set("goal_summary")} placeholder="Describe the specific objective you were hired to achieve" required rows={2} style={{ resize: "vertical" }} />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label className="form-label">How did you do it? (narrative) *</label>
            <textarea data-testid="record-narrative-input" className="form-input" value={form.narrative_summary} onChange={set("narrative_summary")} placeholder="Describe your approach, process, and what made it work" required rows={3} style={{ resize: "vertical" }} />
          </div>

          <div>
            <label className="form-label">Time taken (days) *</label>
            <input data-testid="record-time-input" type="number" className="form-input" value={form.time_taken_days} onChange={set("time_taken_days")} placeholder="e.g. 21" required min={1} />
          </div>

          <div>
            <label className="form-label">Proof URL (case study, etc.)</label>
            <input data-testid="record-proof-input" type="url" className="form-input" value={form.proof_url} onChange={set("proof_url")} placeholder="https://..." />
          </div>

          <div>
            <label className="form-label">Budget min ($) *</label>
            <input data-testid="record-budget-min-input" type="number" className="form-input" value={form.budget_min} onChange={set("budget_min")} placeholder="e.g. 500" required min={0} />
          </div>

          <div>
            <label className="form-label">Budget max ($) *</label>
            <input data-testid="record-budget-max-input" type="number" className="form-input" value={form.budget_max} onChange={set("budget_max")} placeholder="e.g. 2000" required min={0} />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label className="form-label">Result metrics (key outcomes)</label>
            {form.result_metrics.map((m, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8 }}>
                <input data-testid={`metric-key-${i}`} className="form-input" value={m.key} onChange={e => setMetric(i, "key", e.target.value)} placeholder="e.g. booking_increase_pct" style={{ fontSize: "0.82rem" }} />
                <input data-testid={`metric-val-${i}`} className="form-input" value={m.value} onChange={e => setMetric(i, "value", e.target.value)} placeholder="e.g. 32%" style={{ fontSize: "0.82rem" }} />
                {i === form.result_metrics.length - 1 ? (
                  <button type="button" onClick={() => setForm(f => ({ ...f, result_metrics: [...f.result_metrics, { key: "", value: "" }] }))} style={{ background: "rgba(92,174,255,0.1)", border: "1px solid rgba(92,174,255,0.2)", borderRadius: 8, cursor: "pointer", color: "#5CAEFF", fontSize: "1rem", padding: "0 0.5rem" }}>+</button>
                ) : (
                  <button type="button" onClick={() => setForm(f => ({ ...f, result_metrics: f.result_metrics.filter((_, idx) => idx !== i) }))} style={{ background: "rgba(255,51,102,0.08)", border: "1px solid rgba(255,51,102,0.2)", borderRadius: 8, cursor: "pointer", color: "var(--pink)", fontSize: "0.9rem", padding: "0 0.5rem" }}>×</button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" data-testid="record-submit-btn" className="btn-primary" disabled={loading} style={{ padding: "0.6rem 1.4rem", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Submitting..." : "Document success"}
          </button>
        </div>
      </form>
    </motion.div>
  );
}
